import { Badge, Button, Icon, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AddTagModal } from '@hero/components/modals';
import { NON_EDITABLE_AUTOMATION_STATUS } from '@hero/constants';
import { BasicTagContactsTable } from '@hero/organisms';
import { useUpdateAutomation } from '@hero/services';
import {
  useGetContacts,
  useGetTagList,
  useUpdateContact
} from '@hero/services/contact-service/hooks';
import { useNotification } from '@hero/store/hooks';
import { removeRecipientSchema, automationRemoveTagSchema } from '@hero/validations';

import * as S from './style';

function AutomationTagRecipients({ automation }) {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { mutateAsync: updateAutomation } = useUpdateAutomation();
  const { mutateAsync: updateContact } = useUpdateContact();
  const { data: tagList } = useGetTagList(
    {
      filters: {
        ids: automation?.contacts?.tags
      },
      options: {
        includeContacts: true
      }
    },
    {
      enabled: !!automation
    }
  );
  const { data: contacts } = useGetContacts(
    {
      filters: {
        tags: automation?.contacts?.tags
      }
    },
    {
      enabled: !!automation
    }
  );

  const totalContactCount = contacts?.docs?.length || 0;
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [isAddTagModalOpen, setIsAddTagModalOpen] = useState(false);

  const isEditable = React.useMemo(
    () => !NON_EDITABLE_AUTOMATION_STATUS.includes(automation?.status),
    [automation]
  );

  const onRemoveTag = tagId => {
    showNotification({
      title: t('areYouSureRemoveTag'),
      message: t('areYouSureRemoveTagMessage'),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      formData: [
        {
          name: 'confirm',
          title: (
            <Typography variant="body2Bold" style={{ marginBottom: 4 }}>
              {t('removeTagModalConfirmLabel')}
            </Typography>
          ),
          type: 'text',
          maxLength: 10
        }
      ],
      formValidationSchema: automationRemoveTagSchema,
      secondaryButtonProps: { children: t('cancel') },
      primaryButtonProps: {
        color: 'error',
        children: t('yesRemove'),
        onClick: async () => {
          await updateAutomation({
            params: { code: automation.code },
            body: {
              contacts: {
                tags: tagList?.docs?.filter(item => item.id !== tagId)?.map(item => item.id)
              }
            }
          });
        }
      }
    });
  };

  useEffect(() => {
    const tagNames = tagList?.docs?.map(item => item.name);

    if (selectedRecipient && tagList?.docs?.length)
      showNotification({
        title: t('areYouSureRemoveRecipient'),
        message: t('areYouSureRemoveTagRecipientMessage1'),
        notification: {
          title: (
            <Trans
              i18nKey="detachThemFromTagsToRemove"
              values={{ name: tagNames }}
              style={{ overflowWrap: 'anywhere' }}
            />
          ),
          message: t('detachThemFromTagsToRemoveMessage'),
          status: 'info',
          isClosable: true
        },
        notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
        formData: [
          {
            name: 'confirm',
            title: (
              <Typography variant="body2Bold" style={{ marginBottom: 4 }}>
                {t('removeRecipientModalConfirmLabel')}
              </Typography>
            ),
            type: 'text'
          }
        ],
        formValidationSchema: removeRecipientSchema,
        secondaryButtonProps: { children: t('cancel') },
        primaryButtonProps: {
          color: 'error',
          children: t('yesRemove'),
          onClick: async () => {
            await updateContact({
              id: selectedRecipient?.id,
              body: {
                tags: selectedRecipient?.tags?.filter(
                  selectedTag => !tagList?.docs?.map(item => item.id)?.includes(selectedTag.id)
                )
              }
            });
          }
        }
      }),
        setSelectedRecipient(null);
  }, [selectedRecipient, tagList]);

  return (
    <S.RecipientsWrapper>
      <S.RecipientsHeader>
        <Typography variant="body2">
          <Trans
            i18nKey="totalTags"
            values={{ count: automation?.contacts?.tags?.length || 0 }}
            components={{ b: <b /> }}
          />
        </Typography>
        <VerticalDivider $height={24} $margin="0 10px" />
        <S.BadgeWrapper>
          {tagList?.docs?.map(tag => (
            <Badge
              key={tag.id}
              variant="contained"
              color="bgWeak"
              rounded
              text={`${tag.name} (${tag.contactsCount})`}
              size="sm"
              closable
              onClose={() => onRemoveTag(tag.id)}
            />
          ))}
        </S.BadgeWrapper>
        <VerticalDivider $height={24} $margin="0 10px" />
        <Button variant="link" disabled={!isEditable} onClick={() => setIsAddTagModalOpen(true)}>
          <Icon name="add" color="var(--color-accent-primary-default)" />
          {t('addTags')}
        </Button>
      </S.RecipientsHeader>

      <S.RecipientsHeader>
        <Typography variant="body2">
          <Trans
            i18nKey="totalRecipients"
            values={{ count: totalContactCount }}
            components={{ b: <b /> }}
          />
        </Typography>
      </S.RecipientsHeader>
      <BasicTagContactsTable
        tags={automation?.contacts?.tags}
        disabledColumns={[automation?.type === 'birthday' ? 'anniversary' : 'birthday']}
        actionButtons={{
          delete: {
            onClick: setSelectedRecipient
          }
        }}
      />
      {isAddTagModalOpen && (
        <AddTagModal
          openModal={isAddTagModalOpen}
          onRequestClose={() => setIsAddTagModalOpen(false)}
          ids={automation?.contacts?.tags}
          onUpdate={async selectedRows => {
            const tags = Object.keys(selectedRows);
            await updateAutomation({
              params: { code: automation?.code },
              body: {
                contacts: {
                  tags
                }
              }
            });
            setIsAddTagModalOpen(false);
          }}
        />
      )}
    </S.RecipientsWrapper>
  );
}

export { AutomationTagRecipients };
